import React, { useEffect, useMemo, useState } from "react";
import { Dialog, Button, confirmDialog, Skeleton } from "@/components/ui";
import { SmartLinkExtraParam, SmartLinkParamType } from "@/services/bedrock/dtos";
import { SmartLinkServices } from "@/services/bedrock";
import { toast } from "sonner";
import { Plus, BarChart4, CircleAlert, Settings2, Globe } from "lucide-react";
import { ExtraParamCard, ExtraParamForm, VALUE_TYPES } from "@/containers/Coach/SmartLink/ExtraParamsDialog/components";

interface SmartLinkExtraParamsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  smartLinkId?: number;
  isCreatingNewLink?: boolean;
  pendingParams?: Partial<SmartLinkExtraParam>[];
  onPendingParamsChange?: (params: Partial<SmartLinkExtraParam>[]) => void;
}

const DEFAULT_PARAMS: SmartLinkExtraParam[] = [
  { queryKey: "utm_source", valueType: "SOCIAL_NETWORK" },
  { queryKey: "utm_medium", valueType: "STATIC", value: "socialcoach" },
  { queryKey: "utm_campaign", valueType: "SOURCE_NAME" },
  { queryKey: "utm_term", valueType: "PLAYER_GUID" },
  { queryKey: "utm_content", valueType: "SOCIAL_POST" },
];

const EMPTY_PARAMS: Partial<SmartLinkExtraParam>[] = [];

export const SmartLinkExtraParamsDialog: React.FC<SmartLinkExtraParamsDialogProps> = ({
  open,
  onOpenChange,
  smartLinkId,
  isCreatingNewLink,
  pendingParams = EMPTY_PARAMS,
  onPendingParamsChange,
}) => {
  const [orgParams, setOrgParams] = useState<SmartLinkExtraParam[]>([]);
  const [linkParams, setLinkParams] = useState<Partial<SmartLinkExtraParam>[]>(pendingParams);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingParam, setEditingParam] = useState<Partial<SmartLinkExtraParam> | null>(null);

  const isLinkContext = !!smartLinkId || isCreatingNewLink;

  const visibleDefaults = useMemo(() => {
    const customKeys = new Set([...orgParams, ...linkParams].map(p => p.queryKey?.toLowerCase() || ""));
    return DEFAULT_PARAMS.filter(p => !customKeys.has(p.queryKey.toLowerCase()));
  }, [orgParams, linkParams]);

  const loadParams = async () => {
    setLoading(true);
    try {
      const orgData = await SmartLinkServices.getSmartLinkExtraParams();
      setOrgParams(orgData);

      if (smartLinkId) {
        const linkData = await SmartLinkServices.getSmartLinkExtraParams(smartLinkId);
        setLinkParams(linkData);
      } else if (isCreatingNewLink) {
        setLinkParams(pendingParams);
      } else {
        setLinkParams([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load tracking parameters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadParams();
      setShowForm(false);
      setEditingParam(null);
    }
  }, [open, smartLinkId, isCreatingNewLink, pendingParams]);

  const onEdit = (param: Partial<SmartLinkExtraParam>) => {
    setEditingParam(param);
    setShowForm(true);
  };

  const onDelete = async (param: Partial<SmartLinkExtraParam>) => {
    const confirmed = await confirmDialog({
      title: "Delete Parameter?",
      description: `Are you sure you want to delete the tracking parameter "${param.queryKey}"? This will stop it from being appended to future links.`,
      destructive: true,
      confirmLabel: "Delete",
    });

    if (confirmed) {
      if (isCreatingNewLink && !param.extraParamId) {
        const newPending = linkParams.filter(p => p.queryKey !== param.queryKey);
        setLinkParams(newPending);
        onPendingParamsChange?.(newPending);
        toast.success("Parameter removed");
      } else if (param.extraParamId) {
        try {
          await SmartLinkServices.deleteSmartLinkExtraParam(param.extraParamId);
          toast.success("Parameter deleted");
          loadParams();
        } catch (error) {
          toast.error("Failed to delete parameter");
        }
      }
    }
  };

  const handlePendingParamSubmit = (param: Partial<SmartLinkExtraParam>) => {
    let newParams = [...linkParams];
    if (editingParam?.queryKey) {
      newParams = newParams.map(p => (p.queryKey === editingParam.queryKey ? param : p));
    } else {
      newParams.push(param);
    }
    setLinkParams(newParams);
    onPendingParamsChange?.(newParams);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="2xl">
      <Dialog.Header>
        <Dialog.Title>Tracking Parameters</Dialog.Title>
        <Dialog.Description>
          {isLinkContext
            ? "Customize tracking for this Smart Link. Overrides added here will replace the system and organization defaults shown below."
            : "SocialCoach automatically enriches your Smart Links with standard parameters. You can add additional custom parameters here for all links."}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[calc(100vh-20rem)] overflow-y-auto no-scrollbar">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Column 1: System Defaults */}
          <div className="flex flex-col gap-2">
            {visibleDefaults.length > 0 && (
              <>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-1">
                  <Globe className="size-3" />
                  System Defaults
                </h4>
                <span className="text-sm text-muted-foreground italic leading-relaxed">
                  These defaults are set to work automatically with Google Analytics to track every click with zero
                  configuration required.
                </span>
                <div className="grid grid-cols-1 gap-1.5">
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={`default-skel-${i}`} className="h-[40px] w-full rounded-lg" />
                      ))
                    : visibleDefaults.map(p => (
                        <div
                          key={p.queryKey + "-default"}
                          className="flex items-center justify-between py-1.5 px-3 bg-slate-50/50 border border-slate-200 rounded-lg group transition-colors"
                        >
                          <code className="text-xs font-bold text-slate-500">{p.queryKey}</code>
                          <div className="flex items-center gap-2">
                            {p.valueType === "STATIC" ? (
                              <span className="text-xs font-semibold text-slate-500 tracking-wider">"{p.value}"</span>
                            ) : (
                              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded text-primary shadow-sm">
                                <span className="text-xs font-semibold uppercase tracking-tight font-mono">
                                  {VALUE_TYPES.find(v => v.value === p.valueType)?.label}
                                </span>
                                <span className="text-xs font-medium italic opacity-80 whitespace-nowrap">(auto)</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                </div>
              </>
            )}
          </div>

          {/* Column 2: Org Settings & Overrides */}
          <div className="flex flex-col gap-8">
            {/* Organization Settings */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Settings2 className="size-3" />
                  Organization Settings
                </h4>
                {!isLinkContext && !showForm && (
                  <Button
                    size="xs"
                    variant="outline"
                    type="button"
                    onClick={() => {
                      setEditingParam(null);
                      setShowForm(true);
                    }}
                  >
                    <Plus className="size-3 mr-1" />
                    Add Parameter
                  </Button>
                )}
              </div>

              {!isLinkContext && showForm && (
                <ExtraParamForm
                  smartLinkId={smartLinkId}
                  editingParam={editingParam}
                  onUpdate={setEditingParam}
                  handleReload={loadParams}
                  setShowForm={setShowForm}
                  valueType={"SOCIAL_NETWORK"}
                  isCreatingNewLink={isCreatingNewLink}
                  onPendingParamSubmit={handlePendingParamSubmit}
                />
              )}

              <div className="grid grid-cols-1 gap-2">
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={`org-skel-${i}`} className="h-[50px] w-full rounded-xl" />
                  ))
                ) : orgParams.length === 0 && (isLinkContext || !showForm) ? (
                  <div className="text-center p-6 border border-dashed rounded-xl text-muted-foreground text-sm bg-slate-50/30">
                    {!smartLinkId && !isCreatingNewLink ? (
                      <span>
                        Need a custom parameter? Click <b>Add Parameter</b> to replace a default value or add a new one.
                      </span>
                    ) : (
                      <span>No Organization Settings have been added yet.</span>
                    )}
                  </div>
                ) : (
                  orgParams
                    .filter(p => p.extraParamId !== editingParam?.extraParamId)
                    .map(p =>
                      !isLinkContext ? (
                        <ExtraParamCard key={p.extraParamId} p={p} onEdit={onEdit} onDelete={onDelete} />
                      ) : (
                        <ExtraParamCard key={p.extraParamId} p={p} />
                      )
                    )
                )}
              </div>
            </div>

            {/* Link Overrides */}
            {isLinkContext && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <BarChart4 className="size-3" />
                    Link Overrides
                  </h4>
                  {!showForm && (
                    <Button
                      size="xs"
                      variant="outline"
                      type="button"
                      onClick={() => {
                        setEditingParam(null);
                        setShowForm(true);
                      }}
                    >
                      <Plus className="size-3 mr-1" />
                      Add Override
                    </Button>
                  )}
                </div>

                {showForm && (
                  <ExtraParamForm
                    smartLinkId={smartLinkId}
                    editingParam={editingParam}
                    onUpdate={setEditingParam}
                    handleReload={loadParams}
                    setShowForm={setShowForm}
                    valueType={"SOCIAL_NETWORK"}
                    isCreatingNewLink={isCreatingNewLink}
                    onPendingParamSubmit={handlePendingParamSubmit}
                  />
                )}

                <div className="grid grid-cols-1 gap-2">
                  {loading ? (
                    Array.from({ length: 2 }).map((_, i) => (
                      <Skeleton key={`link-skel-${i}`} className="h-[50px] w-full rounded-xl" />
                    ))
                  ) : linkParams.length === 0 && !showForm ? (
                    <div className="text-center p-8 border border-dashed rounded-xl text-muted-foreground text-sm bg-slate-50/30">
                      Need a custom parameter? Click <b>Add Override</b> to replace a default value or add a new one.
                    </div>
                  ) : (
                    linkParams
                      .filter(p => {
                        if (editingParam?.extraParamId && p.extraParamId === editingParam.extraParamId) return false;
                        if (editingParam?.queryKey && p.queryKey === editingParam.queryKey && !p.extraParamId)
                          return false;
                        return true;
                      })
                      .map(p => (
                        <ExtraParamCard key={p.extraParamId || p.queryKey} p={p} onEdit={onEdit} onDelete={onDelete} />
                      ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Footer>
        <Button type="button" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
